import * as React from "react";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  FormLabel,
  IconButton,
  OutlinedInput,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  type Theme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";

import { DIALOG_WIDTH, LS_KEYS } from "../../constants";
import { Schemas, serviceTemplateId } from "../../schemas";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { useCreateService } from "../../hooks/mutation/create/useCreateService";
import { WizardStepper } from "../WizardStepper";
import { ServiceTemplatePicker } from "../ServiceTemplatePicker";
import StandardDialogTitle from "./StandardDialogTitle";
import StandardDialogActions from "./StandardDialogActions";
import { useAlerts } from "../../hooks/query/useAlerts";
import { Kpi } from "../../types";
import { useServiceMap } from "../../hooks/query/useServiceMap";
import { DependenciesManager } from "../DependenciesManager";
import { AlertsPicker } from "../AlertsPicker";
import { useDashboard } from "../../hooks/query/useDashboard";
import { useServiceTemplates } from "../../hooks/query/useServiceTemplates";
import { UsersPicker } from "../UsersPicker";
import {
  buildCriticalKpi,
  CRITICAL_KPI_NAME,
  isCriticalKpiId,
} from "../../criticalKpi";
import { readLocalStorage, writeLocalStorage } from "../../utils/localStorage";

const newFormSchema = Schemas.businessService
  .omit({ status: true })
  .and(z.object({ dependencies: z.array(z.string()), dependents: z.array(z.string()) }));

export type NewServiceData = z.infer<typeof newFormSchema>;

const steps = [
  { title: "Choose Template" },
  { title: "Service Details" },
  { title: "Dependencies" },
  { title: "Map KPIs" },
  { title: "Review" },
];

type KpiAlertMappingInput = {
  alertId: string;
  weight: string;
};

type WizardValues = {
  templateId: string;
  serviceName: string;
  owner: string;
  dependencies: string[];
  dependents: string[];
  slaTarget: string;
  revenueWeight: string;
  businessCriticality: string;
  kpiAlertMappings: Record<string, KpiAlertMappingInput[]>;
  criticalAlertIds: string[];
};

const buildEmptyKpiAlertMapping = (): KpiAlertMappingInput => {
  return {
    alertId: "",
    weight: "0",
  };
};

const splitEvenPercent = (count: number): number[] => {
  if (count <= 0) {
    return [];
  }

  const base = Math.floor(100 / count);
  const remainder = 100 - base * count;

  return Array.from({ length: count }, (_, index) => {
    return base + (index < remainder ? 1 : 0);
  });
};

const buildDefaultKpiAlertMappings = (
  templateKpis: Kpi[],
): Record<string, KpiAlertMappingInput[]> => {
  return Object.fromEntries(
    templateKpis.map((kpi) => [kpi.id, [buildEmptyKpiAlertMapping()]]),
  );
};

const distributeAlertWeightsForKpi = (
  rows: KpiAlertMappingInput[],
): KpiAlertMappingInput[] => {
  const nextRows = rows.map((row) => ({ ...row }));
  const linkedIndices: number[] = [];

  for (const [index, row] of nextRows.entries()) {
    if (!row.alertId) {
      continue;
    }
    linkedIndices.push(index);
  }

  const distributedWeights = splitEvenPercent(linkedIndices.length);
  for (const [position, rowIndex] of linkedIndices.entries()) {
    nextRows[rowIndex] = {
      ...nextRows[rowIndex],
      weight: String(distributedWeights[position] ?? 0),
    };
  }

  return nextRows;
};

const wizardSchema = z.object({
  templateId: serviceTemplateId,
  serviceName: z.string().min(1, "Service name is required"),
  owner: z.string().min(1, "Owner is required"),
  dependencies: z.array(z.string()).default([]),
  dependents: z.array(z.string()).default([]),
  slaTarget: z.coerce.number().min(0).max(100),
  revenueWeight: z.coerce.number(),
  businessCriticality: z.coerce.number(),
  criticalAlertIds: z.array(z.string()).default([]),
  kpiAlertMappings: z
    .record(
      z.string(),
      z.array(
        z.object({
          alertId: z.string(),
          weight: z.string(),
        }),
      ),
    )
    .default({}),
});

const defaultValues: WizardValues = {
  templateId: "",
  serviceName: "",
  owner: "",
  dependencies: [],
  dependents: [],
  slaTarget: "",
  revenueWeight: "",
  businessCriticality: "",
  kpiAlertMappings: {},
  criticalAlertIds: [],
};

const WIZARD_STEP_COUNT = steps.length;

const wizardPersistenceValuesSchema: z.ZodType<WizardValues> = z.object({
  templateId: z.string(),
  serviceName: z.string(),
  owner: z.string(),
  dependencies: z.array(z.string()),
  dependents: z.array(z.string()),
  slaTarget: z.string(),
  revenueWeight: z.string(),
  businessCriticality: z.string(),
  criticalAlertIds: z.array(z.string()),
  kpiAlertMappings: z.record(
    z.string(),
    z.array(
      z.object({
        alertId: z.string(),
        weight: z.string(),
      }),
    ),
  ),
});

const wizardPersistenceSchema = z.object({
  step: z.number().int().min(0).max(WIZARD_STEP_COUNT - 1),
  values: wizardPersistenceValuesSchema,
});

type WizardValuesInput = Partial<Omit<WizardValues, "kpiAlertMappings">> & {
  kpiAlertMappings?: Record<
    string,
    Array<Partial<KpiAlertMappingInput>> | undefined
  >;
};

const normalizeWizardValues = (
  values: WizardValuesInput | undefined,
): WizardValues => {
  const normalizedMappings: Record<string, KpiAlertMappingInput[]> = {};
  const inputMappings = values?.kpiAlertMappings ?? {};

  for (const [kpiId, rows] of Object.entries(inputMappings)) {
    if (!Array.isArray(rows)) {
      continue;
    }
    normalizedMappings[kpiId] = rows.map((row) => ({
      alertId: row?.alertId ?? "",
      weight: row?.weight ?? "0",
    }));
  }

  return {
    ...defaultValues,
    ...values,
    dependencies: values?.dependencies ?? [],
    dependents: values?.dependents ?? [],
    criticalAlertIds: values?.criticalAlertIds ?? [],
    kpiAlertMappings: normalizedMappings,
  };
};

const wizardStorageKey = (dashboardId: string): string =>
  `new-business-service-wizard#${dashboardId}`;

const wizardDraftMemoryCache = new Map<
  string,
  {
    step: number;
    values: WizardValues;
  }
>();

const clamp01 = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  if (value < 0) {
    return 0;
  }
  if (value > 1) {
    return 1;
  }
  return value;
};

const clamp100 = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  if (value < 0) {
    return 0;
  }
  if (value > 100) {
    return 100;
  }
  return value;
};

const parseWeightPercent = (raw: string): number => {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return clamp100(parsed);
};

const parseBoundedWeightPercent = (
  raw: string,
  options?: { allowEmpty?: boolean },
): { value: number; valid: boolean } => {
  const trimmed = raw.trim();
  const allowEmpty = options?.allowEmpty ?? true;
  if (trimmed.length === 0) {
    return { value: 0, valid: allowEmpty };
  }

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
    return { value: 0, valid: false };
  }

  return { value: parsed, valid: true };
};

const parseRatioInput = (raw: string): number => {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    return 0;
  }

  if (parsed > 1) {
    return clamp01(parsed / 100);
  }
  return clamp01(parsed);
};

type KpiMappingValidationById = Record<
  string,
  {
    weightTotal: number;
    hasAlerts: boolean;
    validAlertWeights: boolean;
    validWeight: boolean;
  }
>;

type KpiMappingValidationResult = {
  byKpiId: KpiMappingValidationById;
  hasCriticalOverlap: boolean;
  valid: boolean;
};

const validateMappings = ({
  templateKpis,
  values,
}: {
  templateKpis: Kpi[];
  values: WizardValues;
}): KpiMappingValidationResult => {
  const byKpiId: KpiMappingValidationById = {};
  let allValid = true;

  for (const kpi of templateKpis) {
    const rows = values.kpiAlertMappings[kpi.id] ?? [];
    let weightTotal = 0;
    let hasAlerts = false;
    let validAlertWeights = true;

    for (const row of rows) {
      if (!row.alertId) {
        continue;
      }
      hasAlerts = true;
      const alertWeightValidation = parseBoundedWeightPercent(row.weight);
      if (!alertWeightValidation.valid) {
        validAlertWeights = false;
      }
      weightTotal += alertWeightValidation.value;
    }

    const normalizedTotal = Math.round(weightTotal);
    const validWeight =
      hasAlerts === false
        ? true
        : normalizedTotal === 100 && validAlertWeights;

    byKpiId[kpi.id] = {
      weightTotal: normalizedTotal,
      hasAlerts,
      validAlertWeights,
      validWeight,
    };

    if (!validWeight) {
      allValid = false;
    }
  }

  const regularAlertIds = new Set<string>();
  for (const kpiRows of Object.values(values.kpiAlertMappings ?? {})) {
    for (const row of kpiRows ?? []) {
      if (row.alertId.length > 0) {
        regularAlertIds.add(row.alertId);
      }
    }
  }
  const hasCriticalOverlap = (values.criticalAlertIds ?? []).some((alertId) =>
    regularAlertIds.has(alertId),
  );
  if (hasCriticalOverlap) {
    allValid = false;
  }

  return {
    byKpiId,
    hasCriticalOverlap,
    valid: allValid,
  };
};

const ChooseServiceTemplate: React.FC = () => {
  const { control, setValue } = useFormContextTyped();
  const templateId = useWatch({ control, name: "templateId" });
  const { data: serviceTemplates = [], isLoading: isTemplatesLoading } = useServiceTemplates();

  return (
    <Box>
      <ServiceTemplatePicker
        templates={serviceTemplates}
        selected={templateId}
        loading={isTemplatesLoading}
        onSelect={(value) => {
          const selectedTemplate =
            serviceTemplates.find((template) => template.id === value) ?? null;

          setValue("templateId", value, { shouldDirty: true, shouldValidate: true });
          setValue(
            "kpiAlertMappings",
            selectedTemplate ? buildDefaultKpiAlertMappings(selectedTemplate.kpis) : {},
            {
              shouldDirty: true,
              shouldValidate: true,
            },
          );
          setValue("criticalAlertIds", [], {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
      />
    </Box>
  );
};

const ServiceDetails: React.FC = () => {
  const { control } = useFormContextTyped();

  return (
    <Box component="form" display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" flexDirection="row" gap={2}>
        <FormControl fullWidth>
          <FormLabel>Service name</FormLabel>
          <Controller
            control={control}
            name="serviceName"
            render={({ field }) => (
              <OutlinedInput value={field.value} onChange={field.onChange} />
            )}
          />
        </FormControl>
      </Box>

      <Box display="flex" justifyContent="space-between" flexDirection="row" gap={2}>
        <FormControl fullWidth>
          <FormLabel>Owner</FormLabel>
          <Controller
            control={control}
            name="owner"
            render={({ field }) => (
              <UsersPicker
                value={field.value}
                onChange={(nextOwner) => {
                  field.onChange(nextOwner);
                }}
              />
            )}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>SLA Target (%)</FormLabel>
          <Controller
            control={control}
            name="slaTarget"
            render={({ field }) => (
              <OutlinedInput type="number" value={field.value} onChange={field.onChange} />
            )}
          />
        </FormControl>
      </Box>

      <Box display="flex" justifyContent="space-between" flexDirection="row" gap={2}>
        <FormControl fullWidth>
          <FormLabel>Revenue Weight (%)</FormLabel>
          <Controller
            control={control}
            name="revenueWeight"
            render={({ field }) => (
              <OutlinedInput type="number" value={field.value} onChange={field.onChange} />
            )}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>Business Criticality (%)</FormLabel>
          <Controller
            control={control}
            name="businessCriticality"
            render={({ field }) => (
              <OutlinedInput type="number" value={field.value} onChange={field.onChange} />
            )}
          />
        </FormControl>
      </Box>
    </Box>
  );
};

const DependenciesStep: React.FC<{ serviceMapId?: string }> = ({ serviceMapId }) => {
  const { control } = useFormContextTyped();
  const { data: serviceMap = { nodes: [], edges: [] } } = useServiceMap(serviceMapId);

  return (
    <Box>
      <Controller
        control={control}
        name="dependencies"
        render={({ field: dependenciesField }) => (
          <Controller
            control={control}
            name="dependents"
            render={({ field: dependentsField }) => (
              <DependenciesManager
                serviceMap={serviceMap}
                serviceId={undefined}
                dependencies={dependenciesField.value ?? []}
                dependents={dependentsField.value ?? []}
                onDependenciesChange={(next) => dependenciesField.onChange(next)}
                onDependentsChange={(next) => dependentsField.onChange(next)}
              />
            )}
          />
        )}
      />
    </Box>
  );
};

const useFormContextTyped = () => {
  const methods = React.useContext(FormContextShim);
  if (!methods) {
    throw new Error("Form context not found");
  }
  return methods;
};

const FormContextShim = React.createContext<ReturnType<typeof useForm<WizardValues>> | null>(
  null,
);

const KpiMappings: React.FC<{
  validation: KpiMappingValidationResult | null;
}> = ({ validation }) => {
  const { data: alerts = [] } = useAlerts();
  const { data: serviceTemplates = [] } = useServiceTemplates();
  const { control, watch } = useFormContextTyped();

  const templateIdRaw = watch("templateId") ?? "";
  if (!serviceTemplateId.safeParse(templateIdRaw).success) {
    return <Alert severity="error">Choose a service template first</Alert>;
  }

  const template = serviceTemplates.find((item) => item.id === templateIdRaw);
  if (!template) {
    return <Alert severity="error">Template not found. Select another template.</Alert>;
  }
  const allMappings = watch("kpiAlertMappings") ?? {};
  const criticalAlertIds = watch("criticalAlertIds") ?? [];
  const regularMappedAlertIds = new Set(
    Object.values(allMappings)
      .flatMap((rows) => (Array.isArray(rows) ? rows : []))
      .map((row) => row?.alertId ?? "")
      .filter((alertId) => alertId.length > 0),
  );
  const criticalAlertIdSet = new Set(criticalAlertIds);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Paper sx={{ p: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          KPI weights are automatically distributed equally when the service is
          created.
        </Typography>
      </Paper>

      {template.kpis
        .slice()
        .sort((a, b) => {
          const aCritical = isCriticalKpiId(a.id);
          const bCritical = isCriticalKpiId(b.id);
          if (aCritical === bCritical) {
            return 0;
          }
          return aCritical ? -1 : 1;
        })
        .map((kpi) => {
        const rowsName = `kpiAlertMappings.${kpi.id}` as const;
        const kpiValidation = validation?.byKpiId[kpi.id];

        return (
          <Paper key={kpi.id} sx={{ padding: 2 }}>
            <Stack spacing={1.25}>
              <Typography variant="h3">{kpi.name}</Typography>

              <Controller
                control={control}
                name={rowsName}
                defaultValue={[buildEmptyKpiAlertMapping()]}
                render={({ field }) => {
                  const rows: KpiAlertMappingInput[] =
                    Array.isArray(field.value) && field.value.length > 0
                      ? field.value
                      : [buildEmptyKpiAlertMapping()];

                  const selectedAlertIds = rows
                    .map((row) => row.alertId)
                    .filter((id) => id.length > 0);
                  const remainingAlertCount = alerts.filter((alert) => {
                    return (
                      selectedAlertIds.includes(alert.id) === false &&
                      criticalAlertIdSet.has(alert.id) === false
                    );
                  }).length;
                  const hasEmptyRow = rows.some((row) => row.alertId.length === 0);
                  const canAddRow = remainingAlertCount > 0 && hasEmptyRow === false;

                  const updateRow = (
                    rowIndex: number,
                    patch: Partial<KpiAlertMappingInput>,
                  ) => {
                    const nextRows = rows.map((row, idx) => {
                      if (idx !== rowIndex) {
                        return row;
                      }
                      return { ...row, ...patch };
                    });

                    if (Object.prototype.hasOwnProperty.call(patch, "alertId")) {
                      field.onChange(distributeAlertWeightsForKpi(nextRows));
                      return;
                    }

                    field.onChange(nextRows);
                  };

                  const removeRow = (rowIndex: number) => {
                    if (rows.length <= 1) {
                      return;
                    }
                    const nextRows = rows.filter((_, idx) => idx !== rowIndex);
                    field.onChange(distributeAlertWeightsForKpi(nextRows));
                  };

                  const addRow = () => {
                    field.onChange([...rows, buildEmptyKpiAlertMapping()]);
                  };

                  return (
                    <Stack spacing={1.25}>
                      {rows.map((row, rowIndex) => {
                        const availableAlerts = alerts.filter((alert) => {
                          if (alert.id === row.alertId) {
                            return true;
                          }
                          return (
                            selectedAlertIds.includes(alert.id) === false &&
                            criticalAlertIdSet.has(alert.id) === false
                          );
                        });

                        return (
                          <Stack
                            key={`${kpi.id}-${rowIndex}`}
                            direction={{ xs: "column", md: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", md: "center" }}
                          >
                            <AlertsPicker
                              id={`${kpi.id}-alert-${rowIndex}`}
                              label="Alert"
                              alerts={availableAlerts}
                              multiple={false}
                              value={row.alertId}
                              placeholder="Select or create an alert"
                              width="100%"
                              onChange={(nextAlertId) => {
                                updateRow(rowIndex, {
                                  alertId: nextAlertId,
                                });
                              }}
                            />

                            <FormControl sx={{ width: { xs: "100%", md: 180 } }}>
                              <Tooltip title="Percentage of this alert's impact on the KPI. All weights for a single KPI must sum to 100%.">
                                <FormLabel>Weight (%)</FormLabel>
                              </Tooltip>
                              <OutlinedInput
                                type="number"
                                value={row.weight}
                                onChange={(event) => {
                                  updateRow(rowIndex, {
                                    weight: event.target.value,
                                  });
                                }}
                                inputProps={{
                                  min: 0,
                                  max: 100,
                                }}
                              />
                            </FormControl>

                            {rows.length > 1 && (
                              <IconButton
                                sx={{ alignSelf: { xs: "flex-end", md: "center" } }}
                                onClick={() => {
                                  removeRow(rowIndex);
                                }}
                              >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        );
                      })}

                      {kpiValidation?.hasAlerts && (
                        <Typography
                          variant="body2"
                          color={kpiValidation.validWeight ? "text.secondary" : "error"}
                        >
                          Weight total: {kpiValidation.weightTotal}/100
                        </Typography>
                      )}
                      {kpiValidation?.validAlertWeights === false && (
                        <Typography variant="body2" color="error">
                          Alert weights must be between 0 and 100.
                        </Typography>
                      )}

                      {canAddRow && (
                        <Box>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddRoundedIcon fontSize="small" />}
                            onClick={addRow}
                          >
                            Add
                          </Button>
                        </Box>
                      )}
                    </Stack>
                  );
                }}
              />
            </Stack>
          </Paper>
        );
      })}

      <Paper sx={{ padding: 2 }}>
        <Stack spacing={1.25}>
          <Typography variant="h3">{CRITICAL_KPI_NAME}</Typography>
          <Typography variant="body2" color="text.secondary">
            Any linked alert here can immediately degrade service score.
          </Typography>

          <Controller
            control={control}
            name="criticalAlertIds"
            defaultValue={[]}
            render={({ field }) => {
              const selectedCriticalAlertIds = Array.isArray(field.value)
                ? field.value
                : [];

              return (
                <AlertsPicker
                  id="critical-kpi-alerts"
                  label="Critical alerts"
                  alerts={alerts.filter((alert) => {
                    if (selectedCriticalAlertIds.includes(alert.id)) {
                      return true;
                    }
                    return regularMappedAlertIds.has(alert.id) === false;
                  })}
                  multiple
                  value={selectedCriticalAlertIds}
                  placeholder="Select critical alerts"
                  width="100%"
                  onChange={(nextIds) => {
                    field.onChange(nextIds);
                  }}
                />
              );
            }}
          />

          {validation?.hasCriticalOverlap && (
            <Typography variant="body2" color="error">
              Critical KPI alerts cannot be assigned to regular KPIs.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

const Reviews: React.FC = () => {
  const { watch } = useFormContextTyped();

  const serviceName = watch("serviceName") ?? "";
  const owner = watch("owner") ?? "";
  const revenueWeight = watch("revenueWeight") ?? "";
  const slaTarget = watch("slaTarget") ?? "";
  const businessCriticality = watch("businessCriticality") ?? "";

  return (
    <Paper sx={{ padding: 2 }}>
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="h5" fontWeight="600">
          Service Summary
        </Typography>
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" flexDirection="column" gap={1} flex={2}>
            <Typography variant="body2">Name</Typography>
            <Typography variant="body2">Owner</Typography>
            <Typography variant="body2">Revenue Weight</Typography>
            <Typography variant="body2">SLA Target</Typography>
            <Typography variant="body2">Criticality</Typography>
          </Box>
          <Box display="flex" flexDirection="column" gap={1} flex={3}>
            <Typography>{serviceName}</Typography>
            <Typography>{owner}</Typography>
            <Typography>{revenueWeight}</Typography>
            <Typography>{slaTarget}</Typography>
            <Typography>{businessCriticality}</Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const useContinueEnabled = (
  step: number,
  values: WizardValues,
  mappingValidation: KpiMappingValidationResult | null,
): boolean => {
  if (step === 0) {
    return values.templateId.length > 0;
  }

  if (step === 1) {
    const requiredFilled =
      values.serviceName.length > 0 &&
      values.owner.length > 0 &&
      values.slaTarget.length > 0 &&
      values.revenueWeight.length > 0 &&
      values.businessCriticality.length > 0;

    return requiredFilled;
  }

  if (step === 3) {
    return mappingValidation?.valid ?? false;
  }

  return true;
};

const NewServiceDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  dashboardId: string;
}> = ({ onDialogClosed, dashboardId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [step, setStep] = React.useState<number>(0);

  const { data: dashboard } = useDashboard(dashboardId);
  const serviceMapId = dashboard?.id;

  const { data: serviceTemplates = [], refetch: refetchServiceTemplates } =
    useServiceTemplates();
  const { mutateAsync: createService, isPending } = useCreateService();

  const methods = useForm<WizardValues>({
    defaultValues,
    resolver: zodResolver(
      z.object({
        templateId: z.string(),
        serviceName: z.string(),
        owner: z.string(),
        dependencies: z.array(z.string()),
        dependents: z.array(z.string()),
        slaTarget: z.string(),
        revenueWeight: z.string(),
        businessCriticality: z.string(),
        criticalAlertIds: z.array(z.string()),
        kpiAlertMappings: z.record(
          z.string(),
          z.array(
            z.object({
              alertId: z.string(),
              weight: z.string(),
            }),
          ),
        ),
      }),
    ),
    mode: "onChange",
  });
  const hasHydratedRef = React.useRef(false);
  const skipNextPersistRef = React.useRef(false);
  const draftStorageKey = React.useMemo(
    () => wizardStorageKey(dashboardId),
    [dashboardId],
  );

  const watchedValues = useWatch<WizardValues>({
    control: methods.control,
    defaultValue: defaultValues,
  });

  const values = React.useMemo<WizardValues>(() => {
    return normalizeWizardValues(watchedValues);
  }, [watchedValues]);

  React.useEffect(() => {
    void refetchServiceTemplates();
  }, [refetchServiceTemplates]);

  React.useEffect(() => {
    const memoryDraft = wizardDraftMemoryCache.get(draftStorageKey);
    const persistedDraft =
      memoryDraft ??
      readLocalStorage({
        key: draftStorageKey,
        schema: wizardPersistenceSchema,
      });

    if (persistedDraft) {
      methods.reset(normalizeWizardValues(persistedDraft.values), {
        keepDefaultValues: true,
      });
      setStep(persistedDraft.step);
    } else {
      methods.reset(defaultValues, { keepDefaultValues: true });
      setStep(0);
    }

    setSubmitError(null);
    methods.clearErrors();
    skipNextPersistRef.current = true;
    hasHydratedRef.current = true;
  }, [draftStorageKey, methods]);

  React.useEffect(() => {
    if (!hasHydratedRef.current) {
      return;
    }
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }

    const nextDraft = {
      step,
      values,
    };
    wizardDraftMemoryCache.set(draftStorageKey, nextDraft);

    writeLocalStorage({
      key: draftStorageKey,
      schema: wizardPersistenceSchema,
      value: nextDraft,
    });
  }, [draftStorageKey, step, values]);

  const template = React.useMemo(() => {
    const parsed = serviceTemplateId.safeParse(values.templateId);
    if (!parsed.success) {
      return null;
    }
    return serviceTemplates.find((item) => item.id === parsed.data) ?? null;
  }, [serviceTemplates, values.templateId]);

  const mappingValidation = React.useMemo(() => {
    if (!template) {
      return null;
    }
    return validateMappings({
      templateKpis: template.kpis,
      values,
    });
  }, [template, values]);

  const resetWizard = React.useCallback(() => {
    hasHydratedRef.current = false;
    skipNextPersistRef.current = false;
    wizardDraftMemoryCache.delete(draftStorageKey);
    localStorage.removeItem(`${LS_KEYS.version}#${draftStorageKey}`);
    setSubmitError(null);
    setStep(0);
    methods.reset(defaultValues, { keepDefaultValues: true });
    methods.clearErrors();
  }, [draftStorageKey, methods]);

  const continueEnabled = useContinueEnabled(step, values, mappingValidation);

  const onClose = () => {
    setSubmitError(null);
    resetWizard();
    onDialogClosed();
  };

  const nextStep = () => {
    setStep((currentStep) => (currentStep < 4 ? currentStep + 1 : currentStep));
  };

  const goToStep = (target: number) => {
    setStep((current) => (target < current ? target : current));
  };

  const getErrorMessage = (err: unknown): string => {
    if (err && typeof err === "object") {
      const errorObj = err as {
        response?: { data?: { message?: unknown } };
        message?: unknown;
      };

      const apiMessage = errorObj.response?.data?.message;
      if (typeof apiMessage === "string") {
        return apiMessage;
      }
      if (typeof errorObj.message === "string") {
        return errorObj.message;
      }
    }

    return "Something went wrong while creating the service.";
  };

  const onSubmit = async (data: NewServiceData) => {
    try {
      setSubmitError(null);

      if (typeof serviceMapId !== "string" || serviceMapId.length === 0) {
        setSubmitError("Missing service map id");
        return;
      }

      await createService({
        dependencies: data.dependencies,
        dependents: data.dependents,
        serviceMapId,
        service: data,
      });

      resetWizard();
      onDialogClosed({
        message: "Service created!",
        severity: "success",
      });
    } catch (err: unknown) {
      setSubmitError(getErrorMessage(err));
    }
  };

  const createBusinessService = async () => {
    const wizardValues = methods.getValues();

    const parsedWizard = wizardSchema.safeParse({
      templateId: wizardValues.templateId,
      serviceName: wizardValues.serviceName,
      owner: wizardValues.owner,
      dependencies: wizardValues.dependencies,
      dependents: wizardValues.dependents,
      slaTarget: wizardValues.slaTarget,
      revenueWeight: wizardValues.revenueWeight,
      businessCriticality: wizardValues.businessCriticality,
      criticalAlertIds: wizardValues.criticalAlertIds,
      kpiAlertMappings: wizardValues.kpiAlertMappings,
    });

    if (parsedWizard.success === false) {
      const msg = parsedWizard.error.issues.map((issue) => issue.message).join(", ");
      setSubmitError(msg.length > 0 ? msg : "Validation error");
      return;
    }

    const templateConfig = serviceTemplates.find(
      (item) => item.id === parsedWizard.data.templateId,
    );
    if (!templateConfig) {
      setSubmitError("Selected template not found.");
      return;
    }
    const mappingCheck = validateMappings({
      templateKpis: templateConfig.kpis,
      values,
    });

    if (!mappingCheck.valid) {
      setSubmitError(
        "Invalid KPI mapping. Each regular KPI with linked alerts must total 100 and cannot overlap with Critical KPI alerts.",
      );
      return;
    }

    const label = parsedWizard.data.serviceName;
    const id = label.toLowerCase().replaceAll(" ", "_");

    const evenlyDistributedKpiWeights = splitEvenPercent(templateConfig.kpis.length);

    const regularKpis: Kpi[] = templateConfig.kpis.map((kpi, kpiIndex) => {
      const mappings = parsedWizard.data.kpiAlertMappings[kpi.id] ?? [];

      const deduplicated = new Map<string, { alertId: string; weight: number }>();
      for (const mapping of mappings) {
        if (!mapping.alertId) {
          continue;
        }
        const weight = parseWeightPercent(mapping.weight);
        deduplicated.set(mapping.alertId, {
          alertId: mapping.alertId,
          weight,
        });
      }

      const normalizedConnections = Array.from(deduplicated.values());
      const alertsForKpi = normalizedConnections.map((mapping) => mapping.alertId);
      const kpiWeight = evenlyDistributedKpiWeights[kpiIndex] ?? 0;

      return {
        ...kpi,
        alerts: alertsForKpi,
        alertConnections: normalizedConnections,
        weight: kpiWeight,
      };
    });

    const regularMappedAlertIds = new Set(
      regularKpis.flatMap((kpi) => kpi.alerts ?? []),
    );
    const criticalAlertIds = Array.from(
      new Set((parsedWizard.data.criticalAlertIds ?? []).filter((id) => id.length > 0)),
    );
    const overlapAlertIds = criticalAlertIds.filter((alertId) =>
      regularMappedAlertIds.has(alertId),
    );
    if (overlapAlertIds.length > 0) {
      setSubmitError(
        "Critical KPI alerts cannot also be mapped to regular KPIs.",
      );
      return;
    }

    const criticalKpi = buildCriticalKpi(criticalAlertIds);
    const kpis: Kpi[] = [...regularKpis, criticalKpi];

    const raw: z.infer<typeof newFormSchema> = {
      id,
      label,
      owner: parsedWizard.data.owner,
      sla: clamp100(parsedWizard.data.slaTarget),
      revenueWeight: parseRatioInput(String(parsedWizard.data.revenueWeight)),
      businessCritical: parseRatioInput(String(parsedWizard.data.businessCriticality)),
      kpis,
      dependencies: parsedWizard.data.dependencies,
      dependents: parsedWizard.data.dependents,
    };

    const parsed = newFormSchema.safeParse(raw);
    if (parsed.success === false) {
      setSubmitError("Something went wrong during service creation.");
      return;
    }

    await onSubmit(parsed.data);
  };

  const stepContent = (() => {
    if (step === 0) {
      return <ChooseServiceTemplate />;
    }
    if (step === 1) {
      return <ServiceDetails />;
    }
    if (step === 2) {
      return <DependenciesStep serviceMapId={serviceMapId} />;
    }
    if (step === 3) {
      return <KpiMappings validation={mappingValidation} />;
    }
    return <Reviews />;
  })();

  return (
    <FormContextShim.Provider value={methods}>
      <FormProvider {...methods}>
        <Dialog
          open
          onClose={onClose}
          fullWidth
          fullScreen={isSmallScreen}
          PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.xl } }}
        >
          <StandardDialogTitle
            title="Add Business Service"
            isSmallScreen={isSmallScreen}
            onClose={onClose}
          />

          <DialogContent sx={{ overflowX: "hidden" }}>
            <Box display="flex" gap={2} flexDirection="column">
              <WizardStepper steps={steps} onStepChange={goToStep} activeStep={step} />
              {stepContent}
              {submitError && <Alert severity="error">{submitError}</Alert>}
            </Box>
          </DialogContent>

          <StandardDialogActions
            primaryText={step === 4 ? "Create" : "Continue"}
            primaryIcon={<></>}
            onCancel={onClose}
            onPrimaryClicked={step === 4 ? createBusinessService : nextStep}
            loading={isPending}
            disabled={isPending || (step < 4 && continueEnabled === false)}
          />
        </Dialog>
      </FormProvider>
    </FormContextShim.Provider>
  );
};

export default NewServiceDialog;
